import { useState, useMemo } from "react";
import PhoneField from "../components/PhoneField.jsx";

/* =========================================================================
   CONTACT SECTION — Snappy-style two-column layout.
   Left: bold "Contact Us" + description + email/phone + 3 support columns.
   Right: clean form card — split name fields, iconed email, country-code
   phone, "interested in" select, message textarea with live char counter,
   solid-blue submit pill.

   Light / dark themes supported via the `theme` prop.

   Props:
     - theme          : 'light' | 'dark'   (default 'light')
     - heading        : string             — overrides the H2 ("Contact Us")
     - subjectDefault : string             — preselects "Interested in"
     - availability   : string             — overrides the short description
   ========================================================================= */

const COUNTRY_CODES = ["+91", "+1", "+44", "+61", "+971", "+65", "+81"];
const MSG_MAX = 280;

function ContactSection({
  subjectDefault,
  availability,
  heading,
  theme = "light",
}) {
  const isDark = theme === "dark";

  /* ---- form state (compatible payload with /api/contact.php) ---- */
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    interest: subjectDefault || "",
    message: "",
  });
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const [error, setError] = useState("");

  const API_BASE = import.meta.env.VITE_API_BASE || "/api";
  const update = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus("submitting");
    setError("");
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        interest: form.interest,
        message: form.message,
      };
      const res = await fetch(`${API_BASE}/contact.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        setStatus("success");
        setForm({
          name: "",
          email: "",
          phone: "",
          interest: subjectDefault || "",
          message: "",
        });
      } else {
        setStatus("error");
        setError(data.message || "Something went wrong. Please try again.");
      }
    } catch {
      setStatus("error");
      setError("Could not reach the server. Please email us directly.");
    }
  };

  /* ---- theme tokens ---- */
  const t = useMemo(
    () =>
      isDark
        ? {
            sectionBg: "bg-slate-950",
            sectionExtra: "",
            ambient:
              "radial-gradient(60% 70% at 80% 0%, rgba(99,102,241,0.10), transparent 60%), radial-gradient(50% 60% at 0% 100%, rgba(56,189,248,0.08), transparent 60%)",
            headingText: "text-white",
            bodyText: "text-slate-400",
            mutedText: "text-slate-500",
            linkText: "text-slate-200",
            linkRule: "decoration-slate-700 hover:decoration-white",
            cardBg: "bg-slate-900/60 ring-1 ring-white/10",
            cardShadow: "shadow-[0_30px_80px_-30px_rgba(0,0,0,0.7)]",
            cardTitle: "text-white",
            cardSub: "text-slate-400",
            labelText: "text-slate-400",
            field:
              "border border-white/[0.10] bg-white/[0.04] text-white placeholder:text-slate-500 focus:border-sky-400/60 focus:bg-white/[0.07]",
            fieldIcon: "text-slate-500",
            countryWrap:
              "border border-white/[0.10] bg-white/[0.04] text-white",
            countryDivider: "bg-white/10",
            subtitleColumnHeading: "text-white",
            subtitleColumnBody: "text-slate-400",
          }
        : {
            sectionBg:
              "bg-gradient-to-b from-slate-50 via-[#eef2ff] to-slate-100",
            sectionExtra: "",
            ambient:
              "radial-gradient(60% 70% at 80% 0%, rgba(99,102,241,0.10), transparent 60%), radial-gradient(50% 60% at 0% 100%, rgba(56,189,248,0.08), transparent 60%)",
            headingText: "text-slate-950",
            bodyText: "text-slate-600",
            mutedText: "text-slate-700",
            linkText: "text-slate-900",
            linkRule: "decoration-slate-400 hover:decoration-slate-900",
            cardBg: "bg-white ring-1 ring-slate-200/60",
            cardShadow: "shadow-[0_24px_60px_-30px_rgba(15,23,42,0.20)]",
            cardTitle: "text-slate-900",
            cardSub: "text-slate-500",
            labelText: "text-slate-600",
            field:
              "border border-slate-200 bg-white text-slate-900 placeholder:text-slate-400 focus:border-sky-400 focus:bg-white",
            fieldIcon: "text-slate-400",
            countryWrap: "border border-slate-200 bg-white text-slate-900",
            countryDivider: "bg-slate-200",
            subtitleColumnHeading: "text-slate-900",
            subtitleColumnBody: "text-slate-600",
          },
    [isDark],
  );

  const baseField =
    "block w-full rounded-full px-5 py-4 text-[16px] outline-none transition-all " +
    "focus:ring-2 focus:ring-sky-400/30 sm:py-3.5 sm:text-[14px]";
  const textareaField =
    "block w-full rounded-2xl px-5 py-4 text-[16px] outline-none transition-all resize-none " +
    "focus:ring-2 focus:ring-sky-400/30 sm:text-[14px]";

  const supportLine =
    availability ||
    "Tell us what you're building and we'll come back with a scope, a timeline and a price. Usually within a day.";

  return (
    <section
      id="contact"
      className={"relative overflow-hidden " + t.sectionBg + " py-20 lg:py-28"}
      aria-label="Contact Spectranox"
    >
      {/* Soft ambient washes */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{ background: t.ambient }}
      />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
        <div className="grid gap-14 lg:grid-cols-12 lg:gap-20">
          {/* ============ LEFT — text + contact + 3 sub-columns ============ */}
          <div className="lg:col-span-5">
            <h2
              className={
                "font-display text-[clamp(2.6rem,5.5vw,4.2rem)] font-bold leading-[1.02] tracking-tight " +
                t.headingText
              }
            >
              {heading || "Contact Us"}
            </h2>
            <p
              className={
                "mt-6 max-w-md text-[15px] leading-relaxed " + t.bodyText
              }
            >
              {supportLine}
            </p>

            <ul className="mt-8 space-y-3 text-[15px]">
              <li>
                <a
                  href="mailto:hello@spectranox.com"
                  className={"font-medium " + t.mutedText}
                >
                  hello@spectranox.com
                </a>
              </li>
              <li>
                <a
                  href="tel:+919121351765"
                  className={"font-medium " + t.mutedText + " hover:text-sky-500 transition-colors"}
                >
                  +91 91213 51765
                </a>
              </li>
              <li>
                <a
                  href="#contact"
                  className={
                    "font-medium underline underline-offset-4 " +
                    t.linkText +
                    " " +
                    t.linkRule
                  }
                >
                  Customer Support
                </a>
              </li>
            </ul>
          </div>

          {/* ============ RIGHT — form card ============ */}
          <div className="lg:col-span-7">
            {/* Full-bleed on phones: -mx-6 cancels the section gutter and the
                corner radius drops, so the form reads as a screen rather than a
                card floating on one. */}
            <div
              className={
                "-mx-6 rounded-none px-6 py-9 sm:mx-0 sm:rounded-3xl sm:p-10 " +
                t.cardBg +
                " " +
                t.cardShadow
              }
            >
              {status === "success" ? (
                <div className="py-8 text-center">
                  <span className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500/15 ring-1 ring-emerald-500/30">
                    <svg
                      viewBox="0 0 24 24"
                      className="h-7 w-7 text-emerald-500"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M5 13l4 4L19 7" />
                    </svg>
                  </span>
                  <h3
                    className={
                      "mt-5 font-display text-2xl font-bold " + t.cardTitle
                    }
                  >
                    Thanks for reaching out
                  </h3>
                  <p
                    className={"mx-auto mt-3 max-w-sm text-[14px] " + t.cardSub}
                  >
                    We've received your message and will get back to you within
                    one business day.
                  </p>
                  <button
                    type="button"
                    onClick={() => setStatus("idle")}
                    className={
                      "mt-7 text-[13px] font-medium underline underline-offset-4 " +
                      t.linkText
                    }
                  >
                    Send another enquiry
                  </button>
                </div>
              ) : (
                <form onSubmit={onSubmit}>
                  <div>
                    <h3
                      className={
                        "font-display text-[28px] font-bold tracking-tight " +
                        t.cardTitle
                      }
                    >
                      Get in Touch
                    </h3>
                    <p className={"mt-1 text-[14px] " + t.cardSub}>
                      You can reach us anytime
                    </p>
                  </div>

                  <div className="mt-6 grid gap-3 sm:grid-cols-2 sm:gap-4">
                    <input
                      type="text"
                      name="name"
                      required
                      value={form.name}
                      onChange={update}
                      placeholder="Full name"
                      autoComplete="name"
                      autoCapitalize="words"
                      enterKeyHint="next"
                      className={baseField + " " + t.field}
                    />
                    <PhoneField
                      name="phone"
                      value={form.phone}
                      onChange={(v) => setForm((f) => ({ ...f, phone: v }))}
                      placeholder="Phone number"
                      ariaLabel="Phone number"
                    />
                  </div>

                  <div className="mt-3 grid gap-3 sm:grid-cols-2 sm:gap-4 sm:mt-4">
                    <input
                      type="email"
                      name="email"
                      required
                      value={form.email}
                      onChange={update}
                      placeholder="Your email"
                      autoComplete="email"
                      inputMode="email"
                      autoCapitalize="none"
                      autoCorrect="off"
                      spellCheck="false"
                      enterKeyHint="next"
                      className={baseField + " " + t.field}
                    />
                    <div className="relative">
                      <select
                        name="interest"
                        aria-label="What can we help you with?"
                        value={form.interest}
                        onChange={update}
                        required
                        className={
                          baseField + " appearance-none pr-12 " + t.field
                        }
                        style={{ colorScheme: isDark ? "dark" : "light" }}
                      >
                        <option value="">What can we help you with?</option>
                        <option value="Software Development">
                          Software Development
                        </option>
                        <option value="Website Development">
                          Website Development
                        </option>
                        <option value="App Development">App Development</option>
                        <option value="UI/UX Design">UI / UX Design</option>
                        <option value="AI Automation">AI &amp; Automation</option>
                        <option value="Cyber Security">Cyber Security</option>
                        <option value="Drone Development">
                          Drone Development
                        </option>
                        <option value="IoT Development">IoT Projects</option>
                        <option value="Digital Marketing">
                          Digital Marketing
                        </option>
                        <option value="Social Media Management">
                          Social Media Management
                        </option>
                        <option value="Business Consulting">
                          Business Consulting
                        </option>
                        <option value="Other">Not sure yet</option>
                      </select>
                      <span
                        aria-hidden
                        className={
                          "pointer-events-none absolute right-5 top-1/2 -translate-y-1/2 " +
                          t.fieldIcon
                        }
                      >
                        <svg
                          viewBox="0 0 24 24"
                          className="h-3.5 w-3.5"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M6 9l6 6 6-6" />
                        </svg>
                      </span>
                    </div>
                  </div>

                  {/* message with live char counter */}
                  <div className="relative mt-3 sm:mt-4">
                    <textarea
                      name="message"
                      required
                      rows={4}
                      maxLength={MSG_MAX}
                      value={form.message}
                      onChange={update}
                      placeholder="How can we help?"
                      enterKeyHint="send"
                      className={textareaField + " " + t.field}
                    />
                    <span
                      className={
                        "pointer-events-none absolute bottom-3 right-5 text-[11px] tabular-nums " +
                        t.mutedText
                      }
                    >
                      {form.message.length}/{MSG_MAX}
                    </span>
                  </div>

                  {status === "error" && (
                    <p className="mt-4 rounded-lg bg-rose-100 px-4 py-2.5 text-[13px] text-rose-700 ring-1 ring-rose-200">
                      {error}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={status === "submitting"}
                    className="btn-on-solid mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#1d4ed8] px-6 py-4 text-[16px] font-semibold text-white sm:py-3.5 sm:text-[14px] shadow-[0_12px_30px_-12px_rgba(29,78,216,0.65)] transition-all hover:-translate-y-0.5 hover:bg-[#1e40af] disabled:cursor-not-allowed disabled:opacity-70 disabled:hover:translate-y-0"
                  >
                    {status === "submitting" ? (
                      <>
                        <svg
                          viewBox="0 0 24 24"
                          className="h-4 w-4 animate-spin"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.4"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M21 12a9 9 0 1 1-6.2-8.5" />
                        </svg>
                        Sending…
                      </>
                    ) : (
                      "Submit"
                    )}
                  </button>

                  <p className={"mt-4 text-center text-[12px] " + t.cardSub}>
                    By contacting us, you agree to our{" "}
                    <a
                      href="#"
                      className={"underline underline-offset-2 " + t.linkText}
                    >
                      Terms of service
                    </a>{" "}
                    and{" "}
                    <a
                      href="#"
                      className={"underline underline-offset-2 " + t.linkText}
                    >
                      Privacy Policy
                    </a>
                    .
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}

export default ContactSection;
